import { useCallback, useEffect, useRef, useState } from 'react'

interface UseTableCellScrollOptions {
  rows: Array<{ id: string }>
  delayMs?: number
}

/**
 * Identifies the position of a table cell
 */
export interface CellPosition {
  rowId: string
  cellId: string
}

/**
 * Function signature for scrolling a cell into view
 */
export type ScrollToCell = (position: CellPosition) => void

/**
 * Hook to scroll a table cell into view
 *
 * The optional delay can be used to wait for container resizes to complete before scrolling to ensure the scroll
 * target is calculated against the final dimensions (e.g. when the content viewer column opens as a side column).
 *
 * Usage:
 *   const scrollToCell = useTableCellScroll(tableContainerElement, { rows, delayMs })
 *   scrollToCell({ rowId: '123', cellId: 'abc' })
 */
export function useTableCellScroll(tableContainerElement: HTMLDivElement | null, options: UseTableCellScrollOptions): ScrollToCell {
  const [pendingScroll, setPendingScroll] = useState<CellPosition | null>(null)
  const rowsRef = useRef(options.rows)

  // Keep rows ref in sync with latest data
  useEffect(() => {
    rowsRef.current = options.rows
  }, [options.rows])

  const delayMs = options.delayMs ?? 0

  // Scroll logic: wait for the configured delay, then execute
  useEffect(() => {
    if (!pendingScroll || !tableContainerElement) return

    const { rowId, cellId } = pendingScroll
    const rowIndex = rowsRef.current.findIndex((row) => row.id === rowId)

    // Bail if row not found
    if (rowIndex < 0) {
      setPendingScroll(null)
      return
    }

    let scrollExecuted = false

    const executeScroll = () => {
      if (scrollExecuted) return
      scrollExecuted = true

      // Find the cell element and scroll it into view
      const cellElement = tableContainerElement.querySelector(`tr[data-index="${rowIndex}"] td[id="${cellId}"]`) as HTMLElement
      if (cellElement) {
        cellElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        })
      }

      // Clean up
      setPendingScroll(null)
    }

    const timeoutId = window.setTimeout(executeScroll, delayMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [pendingScroll, tableContainerElement, delayMs])

  const scrollToCell = useCallback((position: CellPosition) => {
    setPendingScroll(position)
  }, [])

  return scrollToCell
}
